import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ChefHat, Download, Eye, Plus, ScanLine, UserPlus, Utensils } from 'lucide-react';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import {
  DocNote,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  ErrorState,
  FormField,
  LoadingState,
  MetricCard,
  MetricRow,
  Modal,
  StatusPill,
  TextField,
  type DataTableColumn,
  FilterBar,
  RangePicker,
} from '../../components';
import { formatDate, formatDateTime, today } from '../../lib/format/dates';
import { useSession } from '../../lib/auth/useSession';
import { useSmallScreen } from '../../lib/util/smallScreen';
import { rangeTitle, useAnalyticsWindow } from '../analytics/parts';
import { formatCount } from '../../lib/format/money';
import { notify } from '../../services/notify';
import { SectionLabel } from '../profiles/people';
import { CampaignPeople } from './CampaignPeople';
import { qrFileName, type Campaign, type CampaignDetail, type CampaignVariant, type QRInventoryRow } from './api';
import { CampaignFields, campaignDraftErrors, draftToPatch, type CampaignDraft } from './CampaignFields';
import { QrFields, qrDraftErrors, qrDraftForLink, qrDraftToInput, type QrDraft, type VariantChoice } from './QrFields';
import {
  useActivateCampaign,
  useAddVariant,
  useCampaign,
  useCampaignPerformance,
  useCampaignSettings,
  useDeactivateCampaign,
  useDuplicateCampaign,
  useEnsureLink,
  useGenerateQR,
  useSaveQrImage,
  usePatchCampaign,
  usePatchVariant,
  useUnarchiveCampaign,
} from './queries';
import {
  CAMPAIGN_LANGS,
  CHANNEL_LABEL,
  COLOR_LABEL,
  CopyLink,
  STATUS_LABEL,
  STATUS_TONE,
  TYPE_LABEL,
  apiLang,
  isAppUrl,
  langLabel,
  sanitizeToken,
  slugError,
  variantLabel,
} from './parts';

/**
 * Campaign detail — its own page, `/campaigns/<id>`, under QR & Campaigns (no
 * extra nav level; Back returns to the list with its filters). Everything
 * shared is a short link: one per variant (or the base link) and language,
 * one per QR code. Variants are added here too; a disabled variant only
 * disappears from the admin — its links and codes keep working. Room to grow:
 * who scanned and who converted go below.
 */
export function CampaignPage() {
  const { campaignId } = useParams({ strict: false }) as { campaignId: string };
  const router = useRouter();
  const navigate = useNavigate();
  const q = useCampaign(campaignId);
  const back = () =>
    router.history.canGoBack() ? router.history.back() : void navigate({ to: '/campaigns' as never });

  if (q.isPending) return <LoadingState label="Loading campaign…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return <CampaignBody detail={q.data} onBack={back} />;
}

function toDraft(c: Campaign): CampaignDraft {
  return {
    name: c.name,
    slug: c.slug,
    type: c.type,
    channel: c.channel,
    localeId: c.localeId ?? '',
    targetUrl: c.targetUrl,
    utmSource: c.utmSource,
    utmTerm: c.utmTerm,
  };
}

/** Link slots the admin can share: the base link (if any) and each enabled variant. */
function linkSlots(c: Campaign): Array<{ variant: string; description: string }> {
  const slots = c.hasBaseLink ? [{ variant: '', description: '' }] : [];
  return [
    ...slots,
    ...c.variants.filter((v) => !v.disabled).map((v) => ({ variant: v.value, description: v.description })),
  ];
}

function CampaignBody({ detail, onBack }: { detail: CampaignDetail; onBack: () => void }) {
  const c = detail.campaign;
  const [editing, setEditing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [goingLive, setGoingLive] = useState(false);
  const deactivate = useDeactivateCampaign(c.id);
  const activate = useActivateCampaign(c.id);
  const unarchive = useUnarchiveCampaign(c.id);
  const lock = useSession().locked('marketing.write');
  // Read-only on a phone: no edit, duplicate, activate / archive, variant or
  // QR code changes. Back, links and QR codes (view, download) stay.
  const small = useSmallScreen();

  return (
    <div className="flex flex-col gap-[18px]">
      <Card surface="panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-[280px]">
            <div className="mb-1.5 flex items-center gap-2.5">
              <StatusPill tone={STATUS_TONE[c.status]} dot={c.status === 'active'}>
                {STATUS_LABEL[c.status]}
              </StatusPill>
              <span className="font-mono text-[13px] text-muted">{c.slug}</span>
            </div>
            <div className="font-heading text-[22px] font-bold tracking-[-.02em] text-brown-deep">{c.name}</div>
            <div className="mt-[3px] text-[13px] text-muted">
              {CHANNEL_LABEL[c.channel]} · source <span className="font-mono">{c.utmSource}</span>
              {c.utmTerm && (
                <>
                  {' '}
                  · term <span className="font-mono">{c.utmTerm}</span>
                </>
              )}{' '}
              · created {formatDate(c.createdAt)}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {!small && (
              <>
                <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setDuplicating(true)}>
                  Duplicate
                </Button>
                {c.status === 'draft' && (
                  <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setGoingLive(true)}>
                    Activate
                  </Button>
                )}
                {c.status === 'archived' ? (
                  <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setGoingLive(true)}>
                    Unarchive
                  </Button>
                ) : (
                  <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setArchiving(true)}>
                    Archive
                  </Button>
                )}
              </>
            )}
            <Button size="sm" onClick={onBack}>
              <ArrowLeft size={14} aria-hidden="true" /> Back
            </Button>
          </div>
        </div>
      </Card>

      {c.status === 'draft' && (
        <DocNote>
          Draft — its links and QR codes open the target URL but track nothing: no visits, sign-ups or scans are
          counted. Activate it when it goes out.
        </DocNote>
      )}

      <PerformanceSection campaign={c} />

      <LinkCards detail={detail} />

      {editing && <EditCampaign campaign={c} hasTraffic={!!detail.hasTraffic} onClose={() => setEditing(false)} />}
      {duplicating && <DuplicateCampaign campaign={c} onClose={() => setDuplicating(false)} />}
      {goingLive && (
        <ConfirmDialog
          title={`${c.status === 'archived' ? 'Unarchive' : 'Activate'} ${c.name}?`}
          body={
            c.status === 'archived'
              ? 'It becomes active again: its links and QR codes go back to the target URL and collect attribution.'
              : 'Its links and QR codes start tracking visits, sign-ups and scans.'
          }
          confirmLabel={c.status === 'archived' ? 'Unarchive' : 'Activate'}
          busy={activate.isPending || unarchive.isPending}
          onClose={() => setGoingLive(false)}
          onConfirm={async () => {
            if (c.status === 'archived') {
              await unarchive.mutateAsync();
              notify.success('Campaign unarchived.');
            } else {
              await activate.mutateAsync();
              notify.success('Campaign activated.');
            }
            setGoingLive(false);
          }}
        />
      )}
      {archiving && (
        <ConfirmDialog
          title={`Archive ${c.name}?`}
          body={
            c.deactivatedRedirectUrl
              ? `Its links and QR codes will send people to ${c.deactivatedRedirectUrl}, and stop collecting attribution.`
              : "Its links and QR codes will send people to the app's home page, and stop collecting attribution."
          }
          confirmLabel="Archive"
          busy={deactivate.isPending}
          onClose={() => setArchiving(false)}
          onConfirm={async () => {
            const res = await deactivate.mutateAsync();
            (res.warnings ?? []).forEach((w) => notify.info(w));
            notify.success('Campaign archived.');
            setArchiving(false);
          }}
        />
      )}
    </div>
  );
}

// --- performance ----------------------------------------------------------------------------

/** YYYY-MM-DD of an instant in the admin's timezone. */
function dayIn(value: string, timeZone?: string): string {
  return new Date(value).toLocaleDateString('en-CA', { timeZone });
}

const pct = (v: number) => `${v.toFixed(1)}%`;

/**
 * This campaign's figures over the range — its whole life by default ("All" runs
 * from its creation day) — as the KPI tiles, then the table row's stats in one
 * responsive card.
 */
function PerformanceSection({ campaign: c }: { campaign: Campaign }) {
  const { profile } = useSession();
  const tz = profile?.timezone;
  const w = useAnalyticsWindow({ range: 'all' });
  const window =
    w.range === 'all'
      ? { startDate: dayIn(c.createdAt, tz), endDate: today(tz) }
      : { startDate: w.window.startDate, endDate: w.window.endDate };
  const since = w.range === 'all' ? `since ${formatDate(c.createdAt)}` : rangeTitle(w.range).toLowerCase();
  const q = useCampaignPerformance(c.id, window);
  const r = q.data;
  const n = (v: number | undefined) => (v !== undefined ? formatCount(v) : q.isError ? '—' : '…');

  const stats: Array<[string, ReactNode]> = [
    ['Type', TYPE_LABEL[c.type]],
    ['Locale', c.localeName || '—'],
    ['Engage', n(r?.engagements)],
    [
      'People',
      r ? (
        <CampaignPeople
          key="people"
          campaignId={c.id}
          registered={r.registeredPeople}
          unregistered={r.unregisteredPeople}
          existing={r.existingPeople}
        />
      ) : (
        n(undefined)
      ),
    ],
    ['Scans', n(r?.scans)],
    ['Signups', n(r?.signups)],
    ['Conv.', r ? pct(r.conversionRatePct) : n(undefined)],
    ['Chef / eater', r ? `${formatCount(r.chefsRecruited)} / ${formatCount(r.eatersRecruited)}` : n(undefined)],
    ['Dishes', n(r?.dishesCreated)],
    [
      'Status',
      <StatusPill key="status" tone={STATUS_TONE[c.status]} dot={c.status === 'active'}>
        {STATUS_LABEL[c.status]}
      </StatusPill>,
    ],
  ];

  return (
    <>
      <FilterBar>
        <RangePicker value={w.range} onChange={w.setRange} />
      </FilterBar>
      <MetricRow>
        <MetricCard
          label="Engagements"
          value={n(r?.engagements)}
          hint={`every link open and scan · ${since}`}
          accent="var(--admin-chart-1)"
          icon={<ScanLine size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Signups"
          value={n(r?.signups)}
          hint={r ? `${pct(r.conversionRatePct)} of ${formatCount(r.uniqueDevices)} people reached` : undefined}
          accent="var(--admin-chart-2)"
          icon={<UserPlus size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Chefs recruited"
          value={n(r?.chefsRecruited)}
          hint={r ? `${formatCount(r.eatersRecruited)} eaters` : undefined}
          accent="var(--admin-chart-3)"
          icon={<ChefHat size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Dishes generated"
          value={n(r?.dishesCreated)}
          hint="by recruited chefs"
          accent="var(--admin-chart-4)"
          icon={<Utensils size={15} aria-hidden="true" />}
        />
      </MetricRow>

      <Card surface="panel">
        <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(84px,1fr))] gap-x-4 gap-y-3">
          {stats.map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-[.14em] text-muted">{label}</dt>
              <dd className="m-0 mt-1 break-words text-sm font-semibold leading-snug tabular-nums text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <DocNote>
        People: registered / not registered / already signed up. Registered is everyone with an account who came through
        this campaign; not registered is everyone else it reached, by device; already signed up is the registered people
        who had an account before they came through. Click the first or last number to see who.
      </DocNote>
    </>
  );
}

// --- link cards -----------------------------------------------------------------------------

/**
 * One card per link: the base link (when the campaign has one), then each
 * variant. A card keeps everything about that link together — its
 * description, its short links per language and its QR codes.
 */
type Slot = { variant: string; variantData?: CampaignVariant };

function slotsOf(c: Campaign): Slot[] {
  return [
    ...(c.hasBaseLink ? [{ variant: '' }] : []),
    ...c.variants.map((v) => ({ variant: v.value, variantData: v })),
  ];
}

/**
 * Creates any missing short link (enabled slot × language) the first time the
 * page shows it; the BE hands out the same link for the same pair every time.
 */
function useEnsureLinks(detail: CampaignDetail) {
  const c = detail.campaign;
  const ensure = useEnsureLink(c.id);
  const asked = useRef(new Set<string>());
  useEffect(() => {
    const links = detail.links ?? [];
    for (const s of linkSlots(c)) {
      for (const lang of CAMPAIGN_LANGS) {
        const key = `${s.variant}|${apiLang(lang)}`;
        const exists = links.some((l) => l.variant === s.variant && l.lang === apiLang(lang));
        if (!exists && !asked.current.has(key)) {
          asked.current.add(key);
          ensure.mutate({ variant: s.variant, lang: apiLang(lang) });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the link set changes
  }, [detail]);
}

function LinkCards({ detail }: { detail: CampaignDetail }) {
  useEnsureLinks(detail);
  const c = detail.campaign;
  const slots = slotsOf(c);
  const base = slots.filter((s) => !s.variantData);
  const variants = slots.filter((s) => s.variantData);
  const small = useSmallScreen();
  return (
    <>
      {base.map((s) => (
        <SlotCard key="base" detail={detail} slot={s} />
      ))}
      {!small && <AddVariant campaign={c} />}
      {variants.map((s) => (
        <SlotCard key={s.variant} detail={detail} slot={s} />
      ))}
    </>
  );
}

function AddVariant({ campaign }: { campaign: Campaign }) {
  const add = useAddVariant(campaign.id);
  const lock = useSession().locked('marketing.write');
  const [adding, setAdding] = useState<string | null>(null);
  const next = String.fromCharCode(97 + Math.max(campaign.variantCount, campaign.hasBaseLink ? 1 : 0)).toUpperCase();

  if (adding === null) {
    return (
      <div>
        <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setAdding('')}>
          <Plus size={14} aria-hidden="true" /> Add variant
        </Button>
      </div>
    );
  }
  return (
    <Card surface="panel">
      <SectionLabel>New variant {next}</SectionLabel>
      <div className="flex items-center gap-2">
        <TextField
          surface="white"
          containerClassName="flex-1"
          aria-label="New variant description"
          placeholder="What makes this variant different"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
        />
        <Button
          size="sm"
          disabled={!adding.trim()}
          loading={add.isPending}
          onClick={() => add.mutate(adding.trim(), { onSuccess: () => setAdding(null) })}
        >
          Add
        </Button>
        <Button variant="ghost-dark" size="sm" onClick={() => setAdding(null)}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

function SlotCard({ detail, slot }: { detail: CampaignDetail; slot: Slot }) {
  const c = detail.campaign;
  const v = slot.variantData;
  const patch = usePatchVariant(c.id);
  const lock = useSession().locked('marketing.write');
  const small = useSmallScreen();
  const [editing, setEditing] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [addingQr, setAddingQr] = useState(false);
  const links = (detail.links ?? []).filter((l) => l.variant === slot.variant);
  const codes = (detail.qrCodes ?? []).filter((q) => (q.placement ?? '') === slot.variant);
  const disabled = !!v?.disabled;
  const last = v?.events.length ? v.events[v.events.length - 1] : undefined;

  return (
    <Card surface="panel" className={disabled ? 'opacity-70' : undefined}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[280px]">
          <div className="flex items-center gap-2.5">
            <h3 className="m-0 font-heading text-[19px] font-bold tracking-[-.01em] text-brown-deep">
              {v ? `Variant ${v.value.toUpperCase()}` : 'Base link'}
            </h3>
            {disabled && <StatusPill tone="neutral">Disabled</StatusPill>}
          </div>
          {v && editing === null && (
            <div className="mt-1 text-sm text-ink">
              {v.description}
              {last && (
                <span
                  className="ml-2 text-xs text-muted"
                  title={v.events.map((e) => `${e.action} ${formatDateTime(e.at)}`).join('\n')}
                >
                  {last.action} {formatDate(last.at)}
                </span>
              )}
            </div>
          )}
          {!v && <div className="mt-1 text-[13px] text-muted">The campaign's plain link, without utm_content.</div>}
          {v && editing !== null && (
            <div className="mt-2 flex items-center gap-2">
              <TextField
                surface="white"
                containerClassName="flex-1"
                aria-label={`Variant ${v.value.toUpperCase()} description`}
                value={editing}
                onChange={(e) => setEditing(e.target.value)}
              />
              <Button
                size="sm"
                loading={patch.isPending}
                disabled={!editing.trim()}
                onClick={() =>
                  patch.mutate({ value: v.value, description: editing.trim() }, { onSuccess: () => setEditing(null) })
                }
              >
                Save
              </Button>
              <Button variant="ghost-dark" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          )}
        </div>
        {v && editing === null && !small && (
          <div className="flex shrink-0 gap-2">
            <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setEditing(v.description)}>
              Edit
            </Button>
            <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setToggling(true)}>
              {disabled ? 'Enable' : 'Disable'}
            </Button>
          </div>
        )}
      </div>

      <SectionLabel>Links</SectionLabel>
      {disabled ? (
        <div className="mb-4 text-[13px] text-muted">Hidden while disabled. Links already shared keep working.</div>
      ) : (
        <div className="mb-4 divide-y divide-[rgba(143,68,32,.08)] rounded-[10px] border border-panel-border">
          {CAMPAIGN_LANGS.map((lang) => {
            const l = links.find((x) => x.lang === apiLang(lang));
            return (
              <div
                key={lang}
                className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] items-center gap-3 px-3.5 py-2 text-[13px] lg:grid-cols-[minmax(0,1fr)_minmax(380px,2fr)_minmax(0,1fr)]"
              >
                <span className="text-muted">{langLabel(lang)}</span>
                {l ? (
                  <CopyLink url={l.shortUrl} label={`${variantLabel(slot.variant)} ${langLabel(lang)} link`} />
                ) : (
                  <span className="text-muted">Preparing…</span>
                )}
                <span className="text-right tabular-nums text-muted">
                  {l ? `${formatCount(l.hitCount)} opens` : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <SectionLabel>QR codes</SectionLabel>
      {codes.length > 0 && (
        <DataTable
          label={`${v ? `Variant ${v.value.toUpperCase()}` : 'Base link'} QR codes`}
          compact
          minWidth={680}
          columns={qrColumns()}
          rows={codes}
          getRowId={(r) => r.qrCodeId}
          emptyLabel="No QR codes yet."
        />
      )}
      {codes.length === 0 && !addingQr && <div className="text-[13px] text-muted">No QR codes yet.</div>}
      {addingQr ? (
        <AddQrCode detail={detail} variant={slot.variant} onDone={() => setAddingQr(false)} />
      ) : (
        !disabled &&
        !small && (
          <div className="mt-2.5">
            <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => setAddingQr(true)}>
              <Plus size={14} aria-hidden="true" /> Add QR code
            </Button>
          </div>
        )
      )}

      {toggling && v && (
        <ConfirmDialog
          title={`${v.disabled ? 'Enable' : 'Disable'} variant ${v.value.toUpperCase()}?`}
          body={
            v.disabled
              ? 'Its links will show here again.'
              : 'Its links are hidden here. Anything already shared or printed keeps working.'
          }
          confirmLabel={v.disabled ? 'Enable' : 'Disable'}
          busy={patch.isPending}
          onClose={() => setToggling(false)}
          onConfirm={() =>
            patch.mutate({ value: v.value, disabled: !v.disabled }, { onSuccess: () => setToggling(false) })
          }
        />
      )}
    </Card>
  );
}

// --- QR codes -----------------------------------------------------------------------------

function qrColumns(): DataTableColumn<QRInventoryRow>[] {
  return [
    { id: 'color', header: 'Color', width: '1fr', cell: (r) => COLOR_LABEL[r.color] ?? r.color },
    { id: 'lang', header: 'Language', width: '1fr', cell: (r) => langLabel(r.lang) },
    { id: 'type', header: 'Kind', width: '1fr', cell: (r) => (r.type === 'marketing' ? 'Connection' : 'Campaign') },
    { id: 'scans', header: 'Scans', width: '1fr', align: 'right', numeric: true, cell: (r) => formatCount(r.scans) },
    {
      id: 'link',
      header: 'Link',
      width: 'minmax(330px, 2fr)',
      cell: (r) => (r.shortUrl ? <CopyLink url={r.shortUrl} label="QR link" /> : <span className="text-muted">—</span>),
    },
    {
      id: 'png',
      header: '',
      width: '76px',
      align: 'right',
      action: true,
      cell: (r) =>
        r.publicUrl ? (
          <span className="inline-flex gap-1">
            <DownloadQr row={r} />
            <ViewQr row={r} />
          </span>
        ) : null,
    },
  ];
}

/** Saves the code's PNG as `<campaign ID…>.png` (see qrFileName), not its storage UUID. */
function DownloadQr({ row }: { row: QRInventoryRow }) {
  const save = useSaveQrImage();
  const filename = qrFileName(row);
  return (
    <button
      type="button"
      aria-label={`Download ${filename}`}
      title={`Download ${filename}`}
      disabled={save.isPending}
      onClick={() => save.mutate({ publicUrl: row.publicUrl, filename })}
      className="inline-flex h-7 w-7 items-center justify-center rounded-app border-0 bg-transparent text-muted hover:bg-[rgba(143,68,32,.08)] hover:text-orange disabled:opacity-50"
    >
      <Download size={15} aria-hidden="true" />
    </button>
  );
}

/**
 * Shows the code large enough to scan from the screen (desktop or phone), with
 * its short link — for trying a code without printing or downloading it.
 */
function ViewQr({ row }: { row: QRInventoryRow }) {
  const [open, setOpen] = useState(false);
  const what = [variantLabel(row.placement), COLOR_LABEL[row.color] ?? row.color, langLabel(row.lang)].join(' · ');
  return (
    <>
      <button
        type="button"
        aria-label={`View QR code: ${what}`}
        title="View QR code"
        onClick={() => setOpen(true)}
        className="inline-flex h-7 w-7 items-center justify-center rounded-app border-0 bg-transparent text-muted hover:bg-[rgba(143,68,32,.08)] hover:text-orange"
      >
        <Eye size={15} aria-hidden="true" />
      </button>
      {open && (
        <Modal isOpen onClose={() => setOpen(false)} size="md" title="QR code">
          <div className="text-center">
            <div className="mb-3 text-[13px] text-muted">{what}</div>
            <img
              src={row.publicUrl}
              alt={`QR code: ${what}`}
              className="mx-auto block w-full max-w-[340px] rounded-app bg-white"
            />
            {row.shortUrl && <div className="mt-3 break-all font-mono text-[12.5px] text-ink">{row.shortUrl}</div>}
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * Add a QR code to one link (base or variant): pick color and language. When
 * the link already has a connection code, the new one starts with the same
 * connection.
 */
function AddQrCode({ detail, variant, onDone }: { detail: CampaignDetail; variant: string; onDone: () => void }) {
  const c = detail.campaign;
  const generate = useGenerateQR();
  const choices: VariantChoice[] = [{ value: variant, label: variantLabel(variant) }];
  const [draft, setDraft] = useState<QrDraft>(() => qrDraftForLink(variant, detail.qrCodes ?? []));
  const [showErrors, setShowErrors] = useState(false);
  const errors = qrDraftErrors(draft, { variants: choices, existing: detail.qrCodes ?? [] });

  return (
    <div className="mt-3 rounded-[10px] border border-panel-border p-3.5">
      <QrFields draft={draft} onChange={setDraft} variants={choices} errors={showErrors ? errors : {}} fixedVariant />
      {generate.error && <ErrorState error={generate.error} compact />}
      <div className="mt-3.5 flex justify-end gap-2">
        <Button variant="ghost-dark" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={generate.isPending}
          onClick={() => {
            setShowErrors(true);
            if (Object.keys(errors).length) return;
            generate.mutate(qrDraftToInput(draft, c.id), {
              onSuccess: () => {
                notify.success('QR code created.');
                onDone();
              },
            });
          }}
        >
          Generate QR code
        </Button>
      </div>
    </div>
  );
}

// --- edit / duplicate --------------------------------------------------------------------------

function EditCampaign({
  campaign,
  hasTraffic,
  onClose,
}: {
  campaign: Campaign;
  /** Type, channel, locale, source and term are fixed (the BE refuses them too). */
  hasTraffic: boolean;
  onClose: () => void;
}) {
  const settings = useCampaignSettings();
  const patch = usePatchCampaign(campaign.id);
  const before = toDraft(campaign);
  const [draft, setDraft] = useState(before);
  const [redirect, setRedirect] = useState(campaign.deactivatedRedirectUrl);
  const [notAppOk, setNotAppOk] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const errors = campaignDraftErrors(draft, { editing: true });
  const targetChanged = draft.targetUrl.trim() !== campaign.targetUrl;
  const external = targetChanged && !isAppUrl(draft.targetUrl, settings.data?.appDomain);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={`Edit ${campaign.name}`}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={patch.isPending}
            disabled={external && !notAppOk}
            onClick={() => {
              setShowErrors(true);
              if (Object.keys(errors).length) return;
              const body = draftToPatch(before, draft);
              if (redirect.trim() !== campaign.deactivatedRedirectUrl) body.deactivatedRedirectUrl = redirect.trim();
              if (external) body.targetUrlConfirmed = true;
              if (!Object.keys(body).length) return onClose();
              patch.mutate(body, {
                onSuccess: () => {
                  notify.success('Campaign updated.');
                  onClose();
                },
              });
            }}
          >
            Save
          </Button>
        </>
      }
    >
      {hasTraffic && (
        <p className="mb-3.5 mt-0 rounded-[10px] bg-cream-warm px-3.5 py-2.5 text-[13px] text-ink">
          This campaign has traffic, so its type, channel, locale, source and term are fixed — its numbers are reported
          under them. To use different ones, duplicate it.
        </p>
      )}
      <CampaignFields
        draft={draft}
        onChange={setDraft}
        errors={showErrors ? errors : {}}
        editing
        reportingFixed={hasTraffic}
        localeName={campaign.localeName}
      />
      <div className="mt-3.5">
        <FormField
          label="After archiving, send people to"
          htmlFor="campaign-redirect"
          optional
          hint="Where links and codes land once the campaign is archived. Empty = the app's home page."
        >
          <TextField
            id="campaign-redirect"
            surface="white"
            type="url"
            value={redirect}
            onChange={(e) => setRedirect(e.target.value)}
          />
        </FormField>
      </div>
      {targetChanged && (
        <p className="mb-0 mt-3.5 rounded-[10px] bg-cream-warm px-3.5 py-2.5 text-[13px] text-ink">
          Changing the target sends every link already shared and every QR code already printed to the new URL.
        </p>
      )}
      {external && (
        <div className="mt-3">
          <Checkbox
            checked={notAppOk}
            onChange={(e) => setNotAppOk(e.target.checked)}
            label="I know this is not a link to the app."
          />
        </div>
      )}
      {patch.error && <ErrorState error={patch.error} compact />}
    </Modal>
  );
}

function DuplicateCampaign({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const dup = useDuplicateCampaign(campaign.id);
  const [slug, setSlug] = useState(campaign.slug.replace(/_\d{6}$/, '_'));
  const [name, setName] = useState(`${campaign.name} (copy)`);
  const err = slugError(slug);
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={`Duplicate ${campaign.name}`}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!!err || !name.trim()}
            loading={dup.isPending}
            onClick={() =>
              dup.mutate(
                { slug, name: name.trim() },
                {
                  onSuccess: () => {
                    notify.success('Campaign duplicated as a draft.');
                    onClose();
                  },
                },
              )
            }
          >
            Duplicate
          </Button>
        </>
      }
    >
      <p className="mt-0 text-sm text-muted">
        Copies the settings and enabled variants (re-lettered from A) into a new draft. Counts, links and QR codes are
        not copied.
      </p>
      <div className="grid gap-3.5">
        <FormField label="Name" htmlFor="dup-name" required>
          <TextField id="dup-name" surface="white" value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="Campaign ID" htmlFor="dup-slug" required error={slug && err ? err : undefined}>
          <TextField
            id="dup-slug"
            surface="white"
            className="font-mono"
            value={slug}
            onChange={(e) => setSlug(sanitizeToken(e.target.value))}
          />
        </FormField>
      </div>
      {dup.error && <ErrorState error={dup.error} compact />}
    </Modal>
  );
}
